import dayjs from "dayjs";
import { endpoints } from "@/configs/Settings";
import { PostAppointment, PostSlot } from "@/services/fhir";
import { PostWhereby } from "@/services/whereby/PostWhereby";
import type { ProviderAgenda } from "../../affiliate/services/providerAgendaService";
import { InviteActionError } from "../../invites/services/inviteActionError";
import type { TeleconsultoriaActor } from "../../types";
import {
  TELECONSULTORIA_APPOINTMENT_TYPE,
  TELECONSULTORIA_NOW_DURATION_IN_MINUTES,
} from "../../utils/teleconsultoriaAppointment";
import type { BookingPatient } from "../types";
import { inviteTeleconsultorForBooking } from "./inviteTeleconsultorForBooking";

const SLOT_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSSZ";

interface CreateTeleconsultoriaAppointmentParams {
  agenda: ProviderAgenda;
  /** Horário livre escolhido. Sem ele, é "Agora": cria o slot na agenda do teleconsultor. */
  slot?: { id: string; start: string; end?: string };
  patient: BookingPatient;
  /** Profissional da afiliada que solicita (host do atendimento). */
  requester: TeleconsultoriaActor;
  /** HCS do solicitante na afiliada (participant do host, sem CON). */
  requesterHealthcareService: TeleconsultoriaActor;
  /** `<oid>-<cnpj>` da prestadora (destino do convite). */
  providerToken: string;
  /** PractitionerRole do solicitante na afiliada (requester da Task). */
  hostRoleReference: string;
}

/** Horário escolhido ou um slot novo de agora (mesmo padrão do encaixe/pronto atendimento). */
async function resolveSlot(params: CreateTeleconsultoriaAppointmentParams) {
  if (params.slot) {
    const end =
      params.slot.end ||
      dayjs(params.slot.start).add(TELECONSULTORIA_NOW_DURATION_IN_MINUTES, "minute").format(SLOT_DATE_FORMAT);
    return { id: params.slot.id, start: params.slot.start, end };
  }
  const start = dayjs();
  const created = await PostSlot({
    type: TELECONSULTORIA_APPOINTMENT_TYPE,
    scheduleId: params.agenda.scheduleId,
    start: start.format(SLOT_DATE_FORMAT),
    end: start.add(TELECONSULTORIA_NOW_DURATION_IN_MINUTES, "minute").format(SLOT_DATE_FORMAT),
  });
  if (!created?.id) throw new Error("Slot não criado");
  return { id: created.id as string, start: created.start as string, end: created.end as string };
}

/** Teleconsultoria é sempre por vídeo: sem sala, não marca. */
async function createRoom(endDate: string) {
  const response = await PostWhereby({
    endDate: dayjs(endDate).format("YYYY-MM-DDTHH:mm:ss-03:00"),
    roomNamePrefix: `${endpoints?.AppName}-whereby`,
    roomNamePattern: "human-short",
  });
  if (!response?.roomUrl) {
    throw new InviteActionError("Não foi possível criar a sala da teleconsultoria.");
  }
  return [{ addressUrl: response.roomUrl as string, sessionKey: (response.meetingId as string) || "" }];
}

/**
 * Marca a teleconsultoria no horário da prestadora.
 * - Slot da agenda da prestadora (o backend ocupa o horário, como no agendamento comum);
 * - host = solicitante da afiliada (1º Practitioner, sem CON) + HCS dele;
 * - o teleconsultor NÃO entra no Appointment aqui: recebe uma Task de convite
 *   (PractitionerRole dele na prestadora) e só entra quando ACEITAR
 *   (PATCH Practitioner + HCS marcados CON, igual ao convite do atendimento);
 * - Location de teleatendimento da prestadora e sala Whereby;
 * - appointmentType TELECONSULTORIA (identifica nas listas e no dashboard).
 * Se o convite falhar, a reserva é cancelada (não fica horário ocupado sem convite).
 */
export async function createTeleconsultoriaAppointment(
  params: CreateTeleconsultoriaAppointmentParams,
): Promise<fhir5.Appointment> {
  const slot = await resolveSlot(params);
  const virtualService = await createRoom(slot.end);
  const { agenda, patient, requester } = params;
  const patientActor = { reference: patient.reference, display: patient.display };

  const appointment = await PostAppointment({
    typeService: TELECONSULTORIA_APPOINTMENT_TYPE,
    cpf: patient.cpf,
    cpfResponsible: patient.responsibleCpf,
    appointmentIsFree: true,
    appointmentStatus: "booked",
    serviceCost: "",
    schedule: agenda.scheduleId,
    slotInfo: slot,
    patientActor,
    locationActor: agenda.location,
    professionalActor: requester,
    // HCS de quem convida. O HCS do teleconsultor entra no aceite (PATCH, CON).
    healthCareServiceActor: params.requesterHealthcareService,
    virtualService,
  });
  if (!appointment?.id) throw new Error("Appointment não criado");

  // O POST pode não devolver tudo: garante horário, sala e paciente para o convite.
  const created: fhir5.Appointment = {
    ...(appointment as fhir5.Appointment),
    start: appointment.start || slot.start,
    end: appointment.end || slot.end,
    virtualService: appointment.virtualService || virtualService,
    // A Location vai para a Task (filtro do scheduling ao buscar o Appointment).
    participant: appointment.participant?.length
      ? appointment.participant
      : [
          { status: "accepted", actor: patientActor },
          { status: "accepted", actor: agenda.location },
        ],
  };
  await inviteTeleconsultorForBooking({
    appointment: created,
    agenda,
    providerToken: params.providerToken,
    hostRoleReference: params.hostRoleReference,
    hostDisplay: requester.display,
    inviteKind: params.slot ? "scheduled" : "now",
  });
  return created;
}
